import { useEffect, useMemo, useState } from "react";
import { ArrowDownAZ, ArrowUpAZ, Gauge, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" });
  } catch { return iso; }
}
function signedDays(v) {
  if (v === null || v === undefined) return "—";
  const r = Math.round(v);
  return `${r > 0 ? "+" : ""}${r}d`;
}
function signedHours(v) {
  if (v === null || v === undefined) return "—";
  const r = Math.round(v);
  return `${r > 0 ? "+" : ""}${r}h`;
}

function daysVarianceColor(v) {
  if (v === null || v === undefined) return "text-muted-foreground";
  if (v <= 0) return "text-emerald-400";
  if (v <= 3) return "text-amber-400";
  return "text-red-400";
}
function hoursVarianceColor(v) {
  if (v === null || v === undefined) return "text-muted-foreground";
  if (v <= 0) return "text-emerald-400";
  if (v <= 10) return "text-amber-400";
  return "text-red-400";
}
function deltaBarColor(est, act) {
  if (est === null || est === undefined || act === null || act === undefined) return "#64748B";
  if (act <= est) return "#34D399";
  const over = act - est;
  if (over <= 10) return "#FBBF24";
  return "#F87171";
}

function DeltaSpark({ est, act }) {
  // Dual-bar mini chart: muted estimated bar above, colored actual bar below.
  if ((est === null || est === undefined) && (act === null || act === undefined)) {
    return <span className="text-muted-foreground/50 text-[10px]">—</span>;
  }
  const e = Number(est) || 0;
  const a = Number(act) || 0;
  const scale = Math.max(e, a, 1);
  const ePct = (e / scale) * 100;
  const aPct = (a / scale) * 100;
  const actColor = deltaBarColor(est, act);
  return (
    <div className="inline-flex flex-col gap-[3px] w-20 align-middle" title={`Est ${e.toFixed(1)} → Act ${a.toFixed(1)}`}>
      <div className="relative h-[5px] bg-[#1F2230] overflow-hidden">
        <div className="absolute top-0 left-0 h-full bg-[#64748B]/70" style={{ width: `${ePct}%` }} />
      </div>
      <div className="relative h-[5px] bg-[#1F2230] overflow-hidden">
        <div className="absolute top-0 left-0 h-full" style={{ width: `${aPct}%`, backgroundColor: actColor }} />
      </div>
    </div>
  );
}
function perfBadge(pct) {
  if (pct === null || pct === undefined) {
    return { cls: "bg-zinc-500/10 border-zinc-500/30 text-zinc-400", label: "—" };
  }
  const v = Math.round(pct);
  if (v >= 95) return { cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400", label: `${v}%` };
  if (v >= 80) return { cls: "bg-amber-500/10 border-amber-500/30 text-amber-400", label: `${v}%` };
  return { cls: "bg-red-500/10 border-red-500/30 text-red-400", label: `${v}%` };
}

export default function MoldPerformanceCard({ accent = "#00D4FF", showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [varBand, setVarBand] = useState("");
  const [perfBand, setPerfBand] = useState("");
  const [sortBy, setSortBy] = useState("days");
  const [sort, setSort] = useState("desc");
  const [search, setSearch] = useState("");

  const params = useMemo(() => {
    const p = { preset: filter.preset, sort_by: sortBy, sort, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (varBand) p.variance_band = varBand;
    if (perfBand) p.perf_band = perfBand;
    return p;
  }, [filter, varBand, perfBand, sortBy, sort, showMexico]);

  useEffect(() => {
    if (!interacted) return;
    let cancelled = false;
    setLoading(true);
    api.get("/departments/MOLD/job-performance", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, interacted]);

  const onFilterChange = (v) => { setInteracted(true); setFilter(v); };
  const onVarChange = (v) => { setInteracted(true); setVarBand(v); };
  const onPerfChange = (v) => { setInteracted(true); setPerfBand(v); };
  const onSortByChange = (v) => { setInteracted(true); setSortBy(v); };
  const onSortToggle = () => { setInteracted(true); setSort((s) => (s === "desc" ? "asc" : "desc")); };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter((r) =>
      (r.job_num || "").toLowerCase().includes(q) ||
      (r.job_name || "").toLowerCase().includes(q) ||
      (r.piece_mark || "").toLowerCase().includes(q)
    );
  }, [data.rows, search]);

  const summary = data.summary || {};

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden"
      data-testid="mold-performance-card"
    >
      <span
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}66 100%)` }}
      />

      <div className="px-5 py-4 border-b border-border flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Gauge size={18} style={{ color: accent }} />
            <h3 className="font-heading font-bold text-xl">Mold show — production performance</h3>
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              · Planned vs Actual · {summary.total ?? 0} pieces
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="px-2 py-1 border border-border bg-[#0A0B10]">Coverage</span>
            <span
              className="px-2 py-1 text-[#0A0B10] font-mono font-semibold"
              style={{ backgroundColor: accent }}
              data-testid="perf-coverage"
            >
              {summary.coverage_days ?? 0}d
            </span>
          </div>
        </div>
        <DateFilter value={filter} onChange={onFilterChange} />
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border" data-testid="perf-summary">
        <Tile label="Total Pieces" value={interacted ? (summary.total ?? 0) : "—"} accent={accent} testId="perf-tile-total" />
        <Tile label="On Time / Early" value={interacted ? (summary.on_time ?? 0) : "—"} accent="#34D399" sub="variance ≤ 0" testId="perf-tile-ontime" />
        <Tile label="Delayed" value={interacted ? (summary.delayed ?? 0) : "—"} accent="#F87171" sub="variance > 0" testId="perf-tile-delayed" />
        <Tile label="Days Lost"
              value={!interacted ? "—"
                : (summary.days_lost === undefined ? "—"
                  : `${fmtNum(summary.days_lost)}`)}
              sub="business days, per piece mark"
              accent={interacted && (summary.days_lost ?? 0) > 0 ? "#F87171" : "#34D399"}
              testId="perf-tile-days-lost" />
        <Tile label="Avg Hours Variance"
              value={!interacted ? "—"
                : (summary.avg_hours_variance === undefined ? "—"
                  : `${summary.avg_hours_variance > 0 ? "+" : ""}${summary.avg_hours_variance}h`)}
              accent={interacted && (summary.avg_hours_variance ?? 0) > 0 ? "#FBBF24" : "#34D399"}
              testId="perf-tile-avg-hv" />
      </div>

      {/* Filters bar */}
      <div className="px-5 py-3 border-b border-border flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="perf-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job / piece..."
            className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#00D4FF]"
          />
        </div>
        <select data-testid="perf-variance-filter" value={varBand} onChange={(e) => onVarChange(e.target.value)}
                className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#00D4FF]">
          <option value="">All Variance</option>
          <option value="ontime">On Time / Early</option>
          <option value="1to3">1–3 days late</option>
          <option value="4plus">4+ days late</option>
        </select>
        <select data-testid="perf-band-filter" value={perfBand} onChange={(e) => onPerfChange(e.target.value)}
                className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#00D4FF]">
          <option value="">All Hours Performance</option>
          <option value="95plus">95%+</option>
          <option value="80to94">80–94%</option>
          <option value="below80">Below 80%</option>
        </select>
        <div className="flex items-center gap-1.5">
          <select data-testid="perf-sortby" value={sortBy} onChange={(e) => onSortByChange(e.target.value)}
                  className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#00D4FF]">
            <option value="days">Sort: Days Variance</option>
            <option value="hours">Sort: Hours Variance</option>
          </select>
          <button data-testid="perf-sort-toggle"
                  onClick={onSortToggle}
                  className="flex items-center gap-1 bg-[#0A0B10] border border-border px-2.5 py-1.5 text-xs uppercase tracking-widest hover:border-[#00D4FF]/60 transition-colors"
                  title="Toggle sort direction">
            {sort === "desc" ? <ArrowDownAZ size={12} /> : <ArrowUpAZ size={12} />}
            {sort === "desc" ? "↓" : "↑"}
          </button>
        </div>
      </div>

      {/* Empty state */}
      {!interacted && (
        <div className="px-5 py-16 text-center" data-testid="perf-empty-state">
          <Gauge size={32} className="mx-auto text-muted-foreground/40 mb-3" />
          <div className="text-sm text-muted-foreground">Select a filter or date range to load pieces.</div>
          <div className="text-[10px] tracking-widest uppercase text-muted-foreground/60 mt-2">
            Use the date range, variance, or performance filters above
          </div>
          <button
            data-testid="perf-quick-30d"
            onClick={() => { setInteracted(true); setFilter({ preset: "30d" }); }}
            className="mt-5 inline-flex items-center gap-2 text-[#0A0B10] font-semibold text-xs uppercase tracking-widest px-4 py-2 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: accent }}
          >
            Quick load · Last 30 days →
          </button>
        </div>
      )}

      {/* Table */}
      {interacted && (
      <div className="overflow-x-auto" data-testid="perf-table-wrap">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="py-2.5 px-4">Job Num</th>
              <th className="py-2.5 px-4">Job Name</th>
              <th className="py-2.5 px-4">Piece Mark</th>
              <th className="py-2.5 px-4">Est Completion</th>
              <th className="py-2.5 px-4">Actual Completion</th>
              <th className="py-2.5 px-4 text-right" title="Business days lost (Mon–Fri, excluding weekends)">Days Lost</th>
              <th className="py-2.5 px-4 text-right">Estimated Hrs</th>
              <th className="py-2.5 px-4 text-center">Δ</th>
              <th className="py-2.5 px-4 text-right">Actual Hrs</th>
              <th className="py-2.5 px-4 text-right">Hours Variance</th>
              <th className="py-2.5 px-4">Hours Perf</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="py-12 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={11} className="py-12 text-center uppercase tracking-widest text-muted-foreground">No pieces match the filters</td></tr>
            )}
            {!loading && filteredRows.map((r, i) => {
              const hp = perfBadge(r.hours_performance_pct);
              const dl = r.days_lost_business;
              return (
                <tr key={`${r.job_num}-${r.piece_mark}-${i}`} className="border-t border-border/40 hover:bg-[#1A1C28]/40">
                  <td className="py-2 px-4 font-mono" style={{ color: accent }}>{r.job_num || "—"}</td>
                  <td className="py-2 px-4 max-w-[260px] truncate" title={r.job_name}>{r.job_name || "—"}</td>
                  <td className="py-2 px-4 font-mono">{r.piece_mark || "—"}</td>
                  <td className="py-2 px-4 font-mono text-muted-foreground">{fmtDate(r.estimated_completion_date)}</td>
                  <td className="py-2 px-4 font-mono">{fmtDate(r.actual_completion_date)}</td>
                  <td className={`py-2 px-4 font-mono text-right tabular-nums font-semibold ${
                    dl === null || dl === undefined ? "text-muted-foreground/50"
                    : dl === 0 ? "text-emerald-400"
                    : "text-red-400"}`}
                    title="Business days between est and actual completion (Mon–Fri only)"
                    data-testid={`perf-row-${i}-days-lost`}>
                    {dl === null || dl === undefined ? "—" : (dl === 0 ? "0" : `+${dl}`)}
                  </td>
                  <td className="py-2 px-4 font-mono text-right tabular-nums">
                    {r.estimated_hrs === null || r.estimated_hrs === undefined ? "—" : Number(r.estimated_hrs).toFixed(1)}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <DeltaSpark est={r.estimated_hrs} act={r.actual_hrs} />
                  </td>
                  <td className="py-2 px-4 font-mono text-right tabular-nums">
                    {r.actual_hrs === null || r.actual_hrs === undefined ? "—" : Number(r.actual_hrs).toFixed(1)}
                  </td>
                  <td className={`py-2 px-4 font-mono text-right tabular-nums font-semibold ${hoursVarianceColor(r.hours_variance)}`}>
                    {signedHours(r.hours_variance)}
                  </td>
                  <td className="py-2 px-4">
                    <span className={`inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 border rounded-sm font-mono ${hp.cls}`}>
                      {hp.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {interacted && (
      <div className="px-5 py-2.5 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
        <span>Showing {filteredRows.length} of {data.rows?.length ?? 0}</span>
        <span>Sort: {sortBy === "days" ? "Days Variance" : "Hours Variance"} {sort === "desc" ? "↓" : "↑"}</span>
      </div>
      )}
    </section>
  );
}

function Tile({ label, value, accent, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-[#12141D] p-4 flex flex-col gap-1">
      <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{label}</div>
      <div className="font-heading font-bold text-2xl tabular-nums" style={{ color: accent }}>
        {typeof value === "number" ? fmtNum(value) : value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
